import {
  BASE_HOST,
  freeTrialRoutes,
  noAuthRoutes,
  publicPages,
} from "@/helpers/constants";
import openReplay from "@/helpers/openReplay";
import Layout from "@/layouts/Layout.vue";
import { loadZohoScript, removeZohoScript } from "@/plugins/zohoDesk";
import { useAddonStore } from "@/stores/add-ons/index.store";
import { useLiveCallWidgetStore } from "@/stores/add-ons/live-widget.store";
import { useNumberAddressAddOnStore } from "@/stores/add-ons/number-address.store";
import { useAuthStore } from "@/stores/auth.store";
import { useBusinessStore } from "@/stores/businesses.store";
import { useNumberStore } from "@/stores/numbers.store";
import { useTeamStore } from "@/stores/team-member.store";
import { useUsersStore } from "@/stores/users.store";
import { useUtilsStore } from "@/stores/utils.store";
import { notify } from "@kyvg/vue3-notification";
import {
  createRouter,
  createWebHistory,
  RouteLocationNormalized,
  RouteRecordRaw,
} from "vue-router";

const RootView = () => import("@/views/RootView.vue");
const AgentLayout = () => import("@/layouts/AgentLayout.vue");
const ManagerLayout = () => import("@/layouts/ManagerLayout.vue");
const ConnectionView = () =>
  import("@/modules/developer-program/views/ConnectionView.vue");
const DeveloperProfile = () =>
  import("@/modules/developer-program/views/DeveloperProfile.vue");
const WelcomePage = () =>
  import("@/modules/developer-program/views/WelcomePage.vue");
const CallerGreetings = () =>
  import(
    "@/modules/personalization/components/IncomingCallFlow/MobileViewCallerGreetings.vue"
  );

const Dashboard = () => import("@/views/DashboardView.vue");
const Contact = () => import("@/views/ContactView.vue");
const ManagerCallRecords = () =>
  import("@/modules/callRecords/RecordsView.vue");
const CallRecordDetailsView = () =>
  import("@/modules/callRecords/CallRecordDetailsView.vue");
const ManagerContact = () => import("@/modules/contact/ManagerContact.vue");
const Signin = () => import("@/views/SigninView.vue");

const Otp = () => import("@/views/OtpView.vue");
const ForgotPassword = () => import("@/views/ForgotPasswordView.vue");
const EmailVerificationView = () => import("@/views/EmailVerificationView.vue");
const MagicLinkView = () => import("@/views/MagicLinkView.vue");
const ResetPasswordEmailVerificationView = () =>
  import("@/views/ResetEmailVerificationView.vue");
const VerifyEmailView = () => import("@/views/VerifyEmailView.vue");
const ResetPassword = () => import("@/views/ResetPasswordView.vue");
const CallRecords = () => import("@/modules/callRecords/AgentCallRecords.vue");
const Profile = () => import("@/modules/userProfile/AgentProfile.vue");
const Reports = () => import("@/views/ReportsView.vue");
const Report = () => import("@/views/ReportView.vue");
const BillingHome = () => import("@/views/BillingHomeView.vue");
const Billing = () => import("@/views/BillingView.vue");
const Activities = () => import("@/views/ActivityView.vue");
const Signup = () => import("@/views/SignupView.vue");
const CompleteSignup = () =>
  import("@/modules/signup/views/CompleteSignupProcess.vue");
const AgentSignUp = () => import("@/views/AgentSignupView.vue");
const Integrations = () =>
  import("@/modules/integrations/IntegrationsView.vue");
const DynamicView1 = () =>
  import("@/modules/integrations/views/dynamicView1.vue");
const ChromeExtension = () =>
  import("@/modules/integrations/views/ChromeExtension.vue");
const ZohoPhoneBridge = () =>
  import("@/modules/integrations/views/ZohoPhonebridge.vue");
const Zapier = () => import("@/modules/integrations/views/Zapier.vue");
const IntegrationsList = () =>
  import("@/modules/integrations/IntegrationsList.vue");
const CallBack = () => import("@/components/CallBack.vue");
const UserOnboardingStatus = () =>
  import("@/modules/personalization/views/UserOnboardingStatusView.vue");
const OutStandingPaymentPage = () =>
  import("@/views/OutstandingPaymentPage.vue");
const NumberProvision = () =>
  import("@/modules/numberProvisioning/NumberProvisionView.vue");

const AdditionalPurchase = () =>
  import(
    "@/modules/numberProvisioning/AdditionalNumberPurchase/AdditionalNumber.vue"
  );
const TeamView = () => import("@/modules/teamMembers/view/TeamView.vue");
const ManageTags = () => import("@/modules/tags/view/ManageTags.vue");
const ManagersProfile = () =>
  import("@/modules/userProfile/ManagerProfile.vue");
const LazyAffiliateView = () => import("@/views/AffiliateView.vue");
// Personalization
const AddNewCallFlow = () =>
  import("@/modules/personalization/views/AddNewCallFlow.vue");
const IncomingCallFlow = () =>
  import("@/modules/personalization/views/IncomingCallFlow.vue");

const MusicOnHold = () =>
  import("@/modules/music-on-hold/views/MusicOnHoldView.vue");

const MenuOption = () =>
  import(
    "@/modules/personalization/components/MenuOptions/MenuOptionsList.vue"
  );
const CallScript = () => import("@/modules/callScript/CallScriptView.vue");
const ConnectionManager = () =>
  import("@/modules/connection/ConnectionView.vue");
const VideoDemo = () => import("@/modules/video-demos/VideoDemoView.vue");
const AddOnList = () => import("@/modules/add-ons/index.vue");
const AddOns = () => import("@/modules/add-ons/components/AddOnLayout.vue");
const KnowledgeDomainAddon = () =>
  import("@/modules/add-ons/knowledge-base/KnowledgeBase.vue");
const VoiceOtpAddon = () =>
  import("@/modules/add-ons/voice-otp/views/VoiceOtp.vue");
const TextToSpeechAddon = () =>
  import("@/modules/add-ons/text-to-speech/views/TextToSpeech.vue");

const AddressAddonView = () =>
  import("@/modules/add-ons/address/views/AddressAddonView.vue");

const SalesCampaignAddon = () =>
  import("@/modules/add-ons/sales-campaign/views/SalesCampaign.vue");
const CampaignOverview = () =>
  import("@/modules/add-ons/sales-campaign/views/CampaignOverview.vue");
const AgentCampaign = () =>
  import("@/modules/add-ons/sales-campaign/views/AgentSalesCampaign.vue");
const AgentCampaignOverview = () =>
  import("@/modules/add-ons/sales-campaign/views/AgentCampaignOverview.vue");

const LiveCallOnly = () =>
  import(
    "@/modules/add-ons/live-call-widget/views/live-call-only/LiveCallOnlyView.vue"
  );
const UpgradeSubscribe = () =>
  import(
    "@/modules/add-ons/live-call-widget/views/live-call-only/UpgradeSubscribe.vue"
  );
const LiveCallOnlyRegistration = () =>
  import(
    "@/modules/add-ons/live-call-widget/views/live-call-only/Registeration.vue"
  );
const LiveCallWidget = () =>
  import("@/modules/add-ons/live-call-widget/views/LiveCallWidget.vue");

const LiveCallWelcome = () =>
  import("@/modules/add-ons/live-call-widget/views/live-call-only/Welcome.vue");
const AddOnOnlyLayout = () =>
  import("@/modules/add-ons/components/AddOnOnlyLayout.vue");

// Developer Program Layout
const DeveloperProgram = () => import("@/layouts/DeveloperProgramLayout.vue");
const DeveloperDashboard = () =>
  import("@/modules/developer-program/views/Dashboard.vue");
const Conversations = () =>
  import("@/modules/conversations/ConversationsView.vue");
const AgentConversations = () =>
  import("@/modules/conversations/AgentConversationView.vue");
const JoinConversation = () =>
  import("@/modules/conversations/JoinConversation.vue");
const GuestConversation = () =>
  import("@/modules/conversations/guestMode/index.vue");
// Developer Program Layout
const PostOnboarding = () =>
  import("@/modules/post-onboarding/view/ManagerOnboarding.vue");

const AgentPostOnboarding = () =>
  import("@/modules/post-onboarding/view/AgentOnboarding.vue");

// go-global routes
const NumberSelectionView = () =>
  import("@/modules/add-ons/address/views/NumberSelectionView.vue");

const AddressSelectionView = () =>
  import("@/modules/add-ons/address/views/AddressSelectionView.vue");

const PlanSelectionView = () =>
  import("@/modules/add-ons/address/views/PlanSelection.vue");

const SetupView = () => import("@/modules/add-ons/address/views/SetupView.vue");

const CartView = () => import("@/modules/add-ons/address/views/CartView.vue");

const PurchaseSuccessfulView = () =>
  import("@/modules/add-ons/address/views/PurchaseSuccessfulView.vue");

const AddressOnlyLayout = () => import("@/layouts/AddressOnlyLayout.vue");

const ThirdPartyVerificationView = () =>
  import("@/modules/userProfile/components/VerificationModals.vue");

const OnboardingTypeForm = () =>
  import("@/modules/personalization/views/OnboardingTypeForm.vue");

const PreSetupCongratulations = () =>
  import("@/views/PreSetupCongratulationsView.vue");

const BusinessesView = () => import("@/views/BusinessView.vue");

const ConnectedDevices = () =>
  import("@/modules/connectedDevices/ConnectedDevicesView.vue");
const PaymentSuccessfulView = () => import("@/views/PaymentSuccessfulView.vue");

const AdditionalSlotView = () => import("@/views/AdditionalSlotView.vue");

const PendingInvitesView = () => import("@/views/PendingInvitesView.vue");

const AddressPurchaseLayout = () =>
  import("@/modules/address-purchase/components/AddressPurchaseLayout.vue");

const AddressPurchasePersonalInformationView = () =>
  import("@/modules/address-purchase/PersonalInformationView.vue");

const AddressPurchaseSelectAddressView = () =>
  import("@/modules/address-purchase/SelectAddressView.vue");

const AddressPurchaseMakePaymentView = () =>
  import("@/modules/address-purchase/MakePaymentView.vue");

const AddressPurchasePaymentSuccessfulView = () =>
  import("@/modules/address-purchase/PaymentSuccessfulView.vue");

const routes: Array<RouteRecordRaw> = [
  {
    path: "/",
    name: "Root",
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    component: Layout,
    children: [
      {
        path: "",
        component: RootView,
        name: "RootView",
        meta: { title: "Home" },
      },
      {
        path: "/sign-in",
        name: "Signin",
        component: Signin,
        meta: { title: "Sign In" },
      },

      {
        path: "/otp",
        name: "Otp",
        component: Otp,
        meta: { title: "Verify OTP" },
      },

      {
        path: "/magic-link",
        name: "MagicLink",
        component: MagicLinkView,
        meta: { title: "Magic Link" },
      },
      {
        path: "/sign-up",
        name: "Signup",
        component: Signup,
        meta: { title: "Sign Up" },
      },
      {
        path: "/complete-signup",
        name: "CompleteSignup",
        component: CompleteSignup,
        meta: { title: "Complete Signup" },
      },
      {
        path: "/outstanding-invoice-payment",
        name: "OutStandingPaymentPage",
        component: OutStandingPaymentPage,
        meta: { title: "Outstanding Payment" },
      },
      {
        path: "/callback/:integrationSource",
        name: "CallBack",
        component: CallBack,
        meta: { title: "Integration Callback" },
      },

      {
        path: "/verify-email/:type",
        name: "VerifyEmail",
        component: VerifyEmailView,
        meta: { title: "Verify Email" },
      },
      {
        path: "/verify-signin-token",
        name: "MagicLink",
        component: MagicLinkView,
        meta: { title: "Verify Sign In" },
      },
      {
        path: "/email-verification",
        name: "EmailVerification",
        component: EmailVerificationView,
        meta: { title: "Email Verification" },
      },
      {
        path: "/reset/email-verification",
        name: "ResetEmailverification",
        component: ResetPasswordEmailVerificationView,
        meta: { title: "Reset Email Verification" },
      },
      {
        path: "/reset-password",
        name: "ResetPassword",
        component: ResetPassword,
        meta: { title: "Reset Password" },
      },
      {
        path: "/payment-successful",
        name: "PaymentSuccessful",
        component: PaymentSuccessfulView,
        meta: { title: "Payment Successful" },
      },
      {
        path: "/additional-slot",
        name: "AdditionalSlot",
        component: AdditionalSlotView,
        meta: { title: "Additional Slot" },
      },
      {
        path: "/pending-invites",
        name: "PendingInvites",
        component: PendingInvitesView,
        meta: { title: "Pending Invites" },
      },
      {
        path: "/home",
        redirect: (to) => {
          if (to.query.trxref && to.query.reference) {
            return "/email-verification";
          }
          return "/";
        },
      },
      {
        path: "/affiliate",
        name: "AffiliateView",
        component: LazyAffiliateView,
        meta: { title: "Affiliate Program" },
      },

      {
        path: "/welcome",
        name: "WelcomePage",
        component: WelcomePage,
        meta: { title: "Welcome" },
      },
      {
        path: "/number-selection",
        name: "NumberPurchase",
        component: NumberSelectionView,
        meta: { title: "Select Number" },
      },
      {
        path: "/address-selection",
        name: "AddressSelectionView",
        component: AddressSelectionView,
        meta: { title: "Select Address" },
      },
      {
        path: "/plan-selection",
        name: "PlanSelectionView",
        component: PlanSelectionView,
        meta: { title: "Select Plan" },
      },
      {
        path: "/business-setup",
        name: "SetupView",
        component: SetupView,
        meta: { title: "Business Setup" },
      },
      {
        path: "/purchase-cart",
        name: "CartView",
        component: CartView,
        meta: { title: "Purchase Cart" },
      },
      {
        path: "/purchase-successful",
        name: "PurchaseSuccessful",
        component: PurchaseSuccessfulView,
        meta: { title: "Purchase Successful" },
      },
      {
        path: "/verify-nin",
        name: "ThirdPartyVerificationView",
        component: ThirdPartyVerificationView,
        meta: { title: "Verify Identity" },
      },
      {
        path: "/call-record-details",
        name: "CallRecordDetailsView",
        component: CallRecordDetailsView,
        meta: { title: "Call Record Details" },
      },
      {
        path: "/forgot-password",
        name: "ForgotPassword",
        component: ForgotPassword,
        meta: { title: "Forgot Password" },
      },
      {
        path: "/typeform",
        name: "OnboardingTypeForm",
        component: OnboardingTypeForm,
        meta: { title: "Onboarding" },
      },
      {
        path: "/agent/signup",
        name: "AgentSignUp",
        component: AgentSignUp,
        meta: { title: "Agent Sign Up" },
      },

      {
        path: "/join-conversation",
        name: "JoinConversation",
        component: JoinConversation,
        meta: { title: "Join Conversation" },
      },
      {
        path: "/guest-conversation",
        name: "GuestConversation",
        component: GuestConversation,
        meta: { title: "Guest Conversation" },
      },

      // Personalization
      {
        path: "provision-number",
        name: "NumberProvisionFLow",
        component: NumberProvision,
        meta: { title: "Provision Number" },
      },
      {
        path: "pre-setup-congratulations",
        name: "PreSetupCongratulations",
        component: PreSetupCongratulations,
        meta: { title: "Setup Complete" },
      },
      {
        path: "/agents",
        name: "AgentLayout",
        component: AgentLayout,
        meta: { loadZoho: true, title: "Agent Dashboard" },
        children: [
          {
            path: "",
            name: "Dashboard",
            component: Dashboard,
            meta: { title: "Dashboard" },
          },
          {
            path: "profile-update",
            name: "AgentProfile",
            component: Profile,
            meta: { title: "Profile" },
          },
          {
            path: "contact",
            name: "Contact",
            component: Contact,
            meta: { title: "Contacts" },
          },
          {
            path: "call-records",
            name: "Call Logs",
            component: CallRecords,
            meta: { title: "Call Logs" },
          },

          {
            path: "reports",
            name: "Reports",
            component: Reports,
            meta: { title: "Reports" },
          },
          {
            path: "conversations",
            name: "AgentConversations",
            component: AgentConversations,
            meta: { title: "Conversations" },
          },
          {
            path: "demo-videos",
            name: "AgentVideoDemo",
            component: VideoDemo,
            meta: { title: "Demo Videos" },
          },
          {
            path: "engagement",
            name: "AgentPostOnboarding",
            component: AgentPostOnboarding,
            meta: { layout: false, title: "Engagement" },
          },
          {
            path: "connection-status",
            name: "AgentConnection",
            component: ConnectionManager,
            meta: { title: "Connection Status" },
          },
          {
            path: "campaigns",
            name: "AgentCampaign",
            component: AgentCampaign,
            meta: { title: "Campaigns" },
          },
          {
            path: "campaigns/overview",
            name: "AgentCampaignOverview",
            component: AgentCampaignOverview,
            meta: { title: "Campaign Overview" },
          },
        ],
      },

      // ManagerLayout
      {
        path: "/managers",
        name: "ManagerLayout",
        component: ManagerLayout,
        meta: { loadZoho: true, title: "Manager Dashboard" },
        children: [
          {
            path: "activities",
            name: "Activities",
            component: Activities,
            meta: { title: "Activities" },
          },
          {
            path: "contact",
            name: "ManagerContact",
            component: ManagerContact,
            meta: { title: "Contacts" },
          },
          {
            path: "billing",
            name: "BillingBaseView",
            component: BillingHome,
            meta: { title: "Billing" },
          },
          {
            path: "billing/:id/:plan",
            name: "Billing Details",
            component: Billing,
            meta: { title: "Billing Details" },
          },
          {
            path: "reports",
            name: "Report",
            component: Report,
            meta: { title: "Reports" },
          },
          {
            path: "call-records",
            name: "ManagerCallLogs",
            component: ManagerCallRecords,
            meta: { title: "Call Logs" },
          },
          {
            path: "incoming-flow",
            name: "IncomingCallFlow",
            component: IncomingCallFlow,
            meta: { title: "Incoming Call Flow" },
          },
          {
            path: "music-on-hold",
            name: "MusicOnHold",
            component: MusicOnHold,
            meta: { title: "Music On Hold" },
          },
          {
            path: "manage-tags",
            name: "ManageTags",
            component: ManageTags,
            meta: { title: "Manage Tags" },
          },

          {
            path: "call-script",
            name: "CallScript",
            component: CallScript,
            meta: { title: "Call Scripts" },
          },
          {
            path: "user-onboarding-status",
            name: "UserOnboardingStatus",
            component: UserOnboardingStatus,
            meta: { title: "Onboarding Status" },
          },
          {
            path: "demo-videos",
            name: "VideoDemo",
            component: VideoDemo,
            meta: { title: "Demo Videos" },
          },
          {
            path: "initial-user-onboarding",
            name: "InitialUserOnboardingView",
            // component: InitialUserOnboardingView,
            component: ConnectedDevices,
          },
          {
            path: "team",
            name: "TeamView",
            component: TeamView,
            meta: { title: "Team Management" },
          },
          {
            path: "profile-update",
            name: "ManagersProfile",
            component: ManagersProfile,
            meta: { title: "Profile" },
          },
          // Personalization
          {
            path: "add-new-call-flow",
            name: "AddNewCallFlow",
            component: AddNewCallFlow,
            meta: { title: "Add Call Flow" },
          },
          {
            path: "edit-call-flow/:id",
            name: "EditCallFlow",
            component: AddNewCallFlow,
            meta: { title: "Edit Call Flow" },
          },
          {
            path: "connection-status",
            name: "ConnectionManager",
            component: ConnectionManager,
            meta: { title: "Connection Status" },
          },
          {
            path: "menu-option",
            name: "MenuOption",
            component: MenuOption,
            meta: { title: "Menu Options" },
          },
          {
            path: "conversations",
            name: "ConversationsView",
            component: Conversations,
            meta: { title: "Conversations" },
          },
          {
            path: "provision-number",
            name: "NumberProvision",
            component: NumberProvision,
            meta: { layout: false, title: "Provision Number" },
          },
          {
            path: "engagement",
            name: "PostOnboarding",
            component: PostOnboarding,
            meta: { layout: false, title: "Engagement" },
          },
          {
            path: "caller-greeting",
            name: "CallerGreetings",
            component: CallerGreetings,
            meta: { layout: false, title: "Caller Greetings" },
          },
          {
            path: "additional-purchase",
            name: "AdditionalPurchase",
            component: AdditionalPurchase,
            meta: { layout: false, title: "Additional Purchase" },
          },
          {
            path: "connected-devices",
            name: "ConnectedDevices",
            component: ConnectedDevices,
            meta: { title: "Connected Devices" },
          },
          // integrate
          {
            path: "integrations",
            name: "Integrations",
            component: Integrations,
            meta: { title: "Integrations" },
            children: [
              {
                path: "",
                name: "IntegrationsList",
                component: IntegrationsList,
                meta: { title: "Integrations" },
              },
              {
                path: "chrome-extension",
                name: "ChromeExtension",
                component: ChromeExtension,
                meta: { title: "Chrome Extension" },
              },
              {
                path: "zoho-phonebridge",
                name: "ZohoPhoneBridge",
                component: ZohoPhoneBridge,
                meta: { title: "Zoho PhoneBridge" },
              },
              {
                path: "zapier",
                name: "Zapier",
                component: Zapier,
                meta: { title: "Zapier Integration" },
              },
              {
                path: ":id",
                name: "DynamicView1",
                component: DynamicView1,
                meta: { title: "Integration" },
                beforeEnter: (to) => {
                  const allowedIntegrationRoutes = [
                    "hubspot-crm",
                    "intercom",
                    "pipe-drive",
                    "fresh-desk",
                    "zoho-crm",
                  ];
                  const params = to?.params?.id as string;
                  if (!allowedIntegrationRoutes.includes(params)) {
                    return false;
                  }
                  return;
                },
              },
            ],
          },
          {
            path: "add-ons",
            name: "AddOns",
            component: AddOns,
            meta: { title: "Add-ons" },
            children: [
              {
                path: "list",
                name: "AddOnsList",
                component: AddOnList,
                meta: { title: "Add-ons" },
              },
              {
                path: "knowledge-base",
                name: "knowledgeBaseAddon",
                component: KnowledgeDomainAddon,
                meta: { title: "Knowledge Base" },
              },
              {
                path: "live-call-widget",
                name: "LiveCallWidget",
                component: LiveCallWidget,
                meta: { title: "Live Call Widget" },
              },
              {
                path: "voice-otp",
                name: "VoiceOtpAddon",
                component: VoiceOtpAddon,
                meta: { title: "Voice OTP" },
              },
              {
                path: "text-to-speech",
                name: "TextToSpeechAddon",
                component: TextToSpeechAddon,
                meta: { title: "Text To Speech" },
              },
              {
                path: "address",
                name: "ManagerAddressAddon",
                component: AddressAddonView,
                meta: { title: "Address Add-on" },
              },
              {
                path: "campaign",
                name: "SalesCampaignAddon",
                component: SalesCampaignAddon,
                meta: { title: "Sales Campaign" },
              },
              {
                component: CampaignOverview,
                path: "campaign/overview",
                name: "CampaignOverview",
                meta: { title: "Campaign Overview" },
              },
            ],
          },
          {
            path: "business",
            name: "BusinessesView",
            component: BusinessesView,
            meta: { title: "Business Management" },
          },
        ],
      },
      // Addon only and free trial layout same layout may be used for subsequent free trial add-ons
      {
        path: "/add-on",
        name: "AddOnOnlyLayout",
        component: AddOnOnlyLayout,
        meta: { title: "Add-on" },
        children: [
          {
            path: "free-live-call",
            name: "LiveCallOnly",
            component: LiveCallOnly,
            meta: { title: "Free Live Call" },
          },

          {
            path: "live-call-free-widget/register-website",
            name: "LiveCallOnlyRegistration",
            component: LiveCallOnlyRegistration,
            meta: { title: "Register Website" },
          },
          {
            path: "live-call-free-widget/welcome",
            name: "LiveCallWelcome",
            component: LiveCallWelcome,
            meta: { title: "Welcome" },
          },
          {
            path: "live-call-free-widget/upgrade-subscription",
            name: "UpgradeSubscribe",
            component: UpgradeSubscribe,
            meta: { title: "Upgrade Subscription" },
          },
        ],
      },
      // Addon only and free trial layout

      // Start Developer Program routes
      {
        path: "/developers",
        name: "DeveloperProgram",
        component: DeveloperProgram,
        meta: { title: "Developer Program" },
        children: [
          {
            path: "dashboard",
            name: "DeveloperDashboard",
            component: DeveloperDashboard,
            meta: { title: "Developer Dashboard" },
          },
          {
            path: "live-call-widget",
            name: "DeveloperLiveCallWidget",
            component: LiveCallWidget,
            meta: { title: "Live Call Widget" },
          },
          // Start Developer Program routes
          {
            path: "voice-otp",
            name: "DeveloperVoiceOtpAddon",
            component: VoiceOtpAddon,
            meta: { title: "Voice OTP" },
          },
          // Start Developer Program routes
          {
            path: "profile-update",
            name: "DeveloperProfile",
            component: DeveloperProfile,
            meta: { title: "Developer Profile" },
          },
          {
            path: "live-call-free-widget/upgrade-subscription",
            name: "DevUpgradeSubscribe",
            component: UpgradeSubscribe,
            meta: { title: "Upgrade Subscription" },
          },
          {
            path: "connection",
            name: "Connections",
            component: ConnectionView,
            meta: { title: "Connections" },
          },
        ],
      },
      // End Developer Program routes

      // start address only layout routes
      {
        path: "/address-user",
        name: "AddressOnlyLayout",
        component: AddressOnlyLayout,
        meta: { title: "Address Management" },
        children: [
          {
            path: "",
            name: "AddressAddon",
            component: AddressAddonView,
            meta: { title: "Address Add-on" },
          },
          {
            path: "billing",
            name: "Billing",
            component: BillingHome,
            meta: { title: "Billing" },
          },
          {
            path: "profile-update",
            name: "Profile",
            component: ManagersProfile,
            meta: { title: "Profile" },
          },
        ],
      },

      {
        path: "/address-purchase",
        name: "AddressPurchaseLayout",
        component: AddressPurchaseLayout,
        meta: { title: "Address Purchase" },
        children: [
          {
            path: "",
            name: "AddressPurchase",
            component: AddressPurchasePersonalInformationView,
            meta: { title: "Personal Information" },
          },
          {
            path: "personal-information",
            name: "AddressPurchasePersonalInformation",
            component: AddressPurchasePersonalInformationView,
            meta: { title: "Personal Information" },
          },
          {
            path: "select-address",
            name: "AddressPurchaseSelectAddress",
            component: AddressPurchaseSelectAddressView,
            meta: { title: "Select Address" },
          },
          {
            path: "make-payment",
            name: "AddressPurchaseMakePayment",
            component: AddressPurchaseMakePaymentView,
            meta: { title: "Make Payment" },
          },
          {
            path: "payment-successful",
            name: "AddressPurchasePaymentSuccessful",
            component: AddressPurchasePaymentSuccessfulView,
            meta: { title: "Payment Successful" },
          },
        ],
      },
    ],
  },

  {
    path: "/:pathMatch(.*)*",
    redirect: "/",
    meta: { title: "Page Not Found" },
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.beforeEach(
  async (to: RouteLocationNormalized, _from: RouteLocationNormalized) => {
    const utilsStore = useUtilsStore();
    const teamStore = useTeamStore();
    const authStore = useAuthStore();
    const userStore = useUsersStore();
    const numberStore = useNumberStore();
    const addonStore = useAddonStore();
    const businessStore = useBusinessStore();
    const liveCallWidgetStore = useLiveCallWidgetStore();
    const numberAddressStore = useNumberAddressAddOnStore();

    // Set document title based on route meta
    const pageTitle = (to.meta?.title as string) || "PressOne";

    document.title =
      pageTitle === "PressOne" ? pageTitle : `PressOne | ${pageTitle}`;

    // Remove existing canonical link if it exists
    const existingCanonical = document.querySelector('link[rel="canonical"]');

    if (existingCanonical) {
      existingCanonical.remove();
    }

    // Generate canonical URL from route path, ensuring trailing slash
    let canonicalPath = to.path;

    if (!canonicalPath.endsWith("/")) {
      canonicalPath += "/";
    }
    const canonicalUrl = `${BASE_HOST}${canonicalPath === "/" ? "/" : canonicalPath}`;

    // Create and append canonical link
    const link = document.createElement("link");

    link.setAttribute("rel", "canonical");
    link.setAttribute("href", canonicalUrl);

    document.head.appendChild(link);

    const loadZoho = to.matched.some((record) => record.meta.loadZoho);

    // Zoho Desk for specific routes
    if (loadZoho) {
      loadZohoScript();
    } else {
      removeZohoScript();
    }

    if (import.meta.env.MODE !== "development") {
      if (userStore.currentUserBusinessLevel) {
        openReplay.setUserID(`${userStore.currentUserBusinessLevel?.email}`);
      } else {
        if (userStore.registerUser.personal_email) {
          openReplay.setUserID(`${userStore.registerUser.personal_email}`);
        }
      }

      await openReplay.start();
    }

    utilsStore.avatarMenu = false;
    utilsStore.menuDrawer = false;

    // redirect to login page if not logged in and trying to access a restricted page
    const paramsPath = to.fullPath.split("?");

    if (paramsPath.length > 0) {
      const params = new URLSearchParams(paramsPath[1]);
      // Display notification
      if (params.get("trxref") && params.get("reference")) {
        if (utilsStore.goGlobalPayment) {
          return "/purchase-successful";
        }

        if (teamStore.pendingInvite) {
          return "/managers/team?pending_draft=true";
        }

        // add this to check if it has redirected before to prevent infinite loop.
        if (!params.get("redirected")) {
          notify({
            text: "Payment successful",
            type: "success",
          });

          authStore.couponCode = "";
          params.set("redirected", "true"); // set to true to prevent it from redirecting in a loop
          return `/sign-in?${params.toString()}`;
        } // redirect to otp from here
      }
    }

    if (to.fullPath.includes("/purchase-successful")) {
      /// redirects if it was not a go global payment
      if (!utilsStore.goGlobalPayment) {
        return "/";
      }
    }

    const authRequired = !publicPages.includes(to.path);

    const includesPublicRoute = (route: string) => to.fullPath.includes(route);

    if (noAuthRoutes.some(includesPublicRoute)) {
      return;
    }

    if (authRequired && !authStore.isAuthenticated) {
      authStore.returnUrl = to.fullPath;

      return "/sign-in";
    }

    if (authStore.isAuthenticated && publicPages.includes(to.path)) {
      const hasAccounts = !!userStore?.userProfile?.length;

      if (!hasAccounts) {
        return;
      }

      if (userStore.canAccessManagerView) {
        return "/managers/connected-devices";
      }

      return "/";
    }

    // Also enables some vital routes to be accessible
    if (
      !numberStore.allNumbersRegisteredUnderABusiness?.length &&
      !to.path.includes("address-user") &&
      !to.path.includes("additional-purchase") &&
      !to.path.includes("provision-number") &&
      numberAddressStore.addresses.length
    ) {
      // redirect to the address only layout
      return "/address-user";
    }

    if (to.fullPath.includes("/managers/")) {
      if (!userStore.canAccessManagerView) {
        return "/";
      }
    }

    // check agent role and redirect to the right page '/' redirects to agent home page
    if (to.fullPath.includes("/agents/")) {
      if (userStore?.canAccessManagerView) {
        return "/managers/connected-devices";
      }
      if (!userStore?.canAccessAgentView) {
        return "/";
      }
    }

    // restriction for users trying to access the live call page with validation
    if (to.fullPath.includes("/live-call-widget")) {
      if (
        liveCallWidgetStore.hasAccess &&
        businessStore.activeProfile?.business?.free_trial_data
          ?.free_trial_expired
      ) {
        const sourceRoute = userStore.currentUserBusinessLevel?.free_trial_data
          ?.is_developer
          ? "/developers/dashboard"
          : "/managers/add-ons/live-call-widget";

        return sourceRoute;
      }

      if (to.fullPath.includes("/knowledge-base")) {
        const hasAccess = addonStore.getAllEnhancedAddons.some(
          (addon) =>
            addon.title?.includes("Knowledge Base Addon") && addon.isPurchased
        );

        if (!hasAccess) {
          return "/manager/connected-devices";
        }
      }

      if (to.fullPath.includes("/voice-otp")) {
        const hasAccess = addonStore.getAllEnhancedAddons.some(
          (addon) =>
            (addon.title?.includes("Voice OTP Addon") && addon.isPurchased) ||
            userStore.currentUserBusinessLevel?.free_trial_data
              ?.is_free_trial_user
        );

        if (!hasAccess) {
          return "/manager/connected-devices";
        }
      }

      if (to.fullPath.includes("/text-to-speech")) {
        const hasAccess = addonStore.getAllEnhancedAddons.some(
          (addon) =>
            addon.title?.includes("Text To Speech Addon") && addon.isPurchased
        );

        if (!hasAccess) {
          return "/manager/connected-devices";
        }
      }

      if (to.fullPath.includes("/campaign")) {
        const hasAccess = addonStore.getAllEnhancedAddons.some(
          (addon) =>
            addon.title?.includes("Sales Campaign Addon") && addon.isPurchased
        );

        if (!hasAccess) {
          return "/manager/connected-devices";
        }
      }

      // Permissions redirection // hide the module from the sidebar but also redirect for users that want to bypass it
      if (to.fullPath.includes("agents/reports")) {
        /// redirects when isnt owner or permissions isnt true
        if (!userStore.getAgentRole?.can_view_activity_report) {
          return "/agents/";
        }
      }

      // Managers
      if (to.fullPath.includes("managers/user-onboarding-status")) {
        /// redirects when isnt owner or permissions isnt true
        if (!userStore.getAgentRole?.can_manage_personalization) {
          return "/managers/connected-devices";
        }
      }

      if (to.fullPath.includes("managers/billing")) {
        /// redirects whenpermissions isnt true
        if (!userStore.getAgentRole?.can_manage_billing) {
          return "/managers/connected-devices";
        }

        const sourceRoute = userStore.currentUserBusinessLevel?.free_trial_data
          ?.is_developer
          ? "/developers/live-call-free-widget/"
          : "/add-on/live-call-free-widget/";

        return `${sourceRoute}upgrade-subscription`;
      }

      if (
        !liveCallWidgetStore.hasAccess &&
        !userStore.currentUserBusinessLevel?.free_trial_data?.is_free_trial_user // redirect this user to dashboard to main page if no access and no free trial
      ) {
        return "/managers/connected-devices";
      }
    }

    // NB if a free trial user tries to access a path not allowed
    if (
      userStore.currentUserBusinessLevel?.free_trial_data?.is_free_trial_user &&
      !freeTrialRoutes.includes(to.path)
    ) {
      const sourceRoute = userStore.currentUserBusinessLevel?.free_trial_data
        ?.is_developer
        ? "/developers/dashboard"
        : "/managers/add-ons/live-call-widget";

      return sourceRoute;
    }

    if (to.fullPath.includes("/knowledge-base")) {
      const hasAccess = addonStore.getAllEnhancedAddons.some(
        (addon) =>
          addon.title?.includes("Knowledge Base Addon") && addon.isPurchased
      );

      if (!hasAccess) {
        return "/manager/connected-devices";
      }
    }

    if (to.fullPath.includes("/voice-otp")) {
      const hasAccess = addonStore.getAllEnhancedAddons.some(
        (addon) =>
          (addon.title?.includes("Voice OTP Addon") && addon.isPurchased) ||
          userStore.currentUserBusinessLevel?.free_trial_data
            ?.is_free_trial_user
      );

      if (!hasAccess) {
        return "/manager/connected-devices";
      }
    }

    if (to.fullPath.includes("/text-to-speech")) {
      const hasAccess = addonStore.getAllEnhancedAddons.some(
        (addon) =>
          (addon.title?.includes("Text To Speech Addon") &&
            addon.isPurchased) ||
          userStore.currentUserBusinessLevel?.free_trial_data
            ?.is_free_trial_user
      );

      if (!hasAccess) {
        return "/manager/connected-devices";
      }
    }

    if (
      to.fullPath.includes("managers/integrations") ||
      to.fullPath.includes("callback")
    ) {
      /// redirects when user not allowed to view integrations
      if (!numberStore.showIntegration) {
        return "/managers/connected-devices";
      }
    }
    if (
      to.fullPath.includes("managers/manage-tags") ||
      to.fullPath.includes("managers/incoming-flow") ||
      to.fullPath.includes("managers/call-script")
    ) {
      /// redirects when  permissions isnt true
      if (!userStore.getAgentRole?.can_manage_personalization) {
        return "/managers/connected-devices";
      }
    }

    if (to.fullPath.includes("agents/engagement")) {
      /// redirects when this form has been filled to main page
      if (userStore.currentUserBusinessLevel?.has_filled_engagement_form) {
        return "/agents/";
      }
    }
  }
);

export default router;
